/**
 * Bundle 101 (URGENT expanded) - Pre-Bundle-89 recipients page.
 *
 * Route: /admin/incident/pre-bundle89-recipients (super_admin only)
 *
 * Lists every invitation whose stored question_set sha256 or
 * fingerprint diverges from the current Bundle 89+ canonical
 * schema. Allows bulk force-reissue on the current schema.
 *
 * Data source:
 *   GET  /api/admin/incident/pre-bundle89-recipients (Bundle 101)
 *   POST /api/admin/incident/pre-bundle89-recipients/resend
 *
 * No em/en dashes, no eslint-disable, no photo/image alt.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { AlertOctagon, Download, Send, Eye, EyeOff, ChevronLeft } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';

const RESP_BADGE = {
  submitted: 'bg-red-50 text-red-800 border-red-200',
  in_progress: 'bg-yellow-50 text-yellow-800 border-yellow-200',
  invited: 'bg-neutral-100 text-neutral-700 border-neutral-200',
  no_response: 'bg-neutral-100 text-neutral-500 border-neutral-200',
};

const AdminIncidentPreBundle89Page = () => {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unmasked, setUnmasked] = useState(false);
  const [onlySubmitted, setOnlySubmitted] = useState(false);
  const [selected, setSelected] = useState({});
  const [signerFirst, setSignerFirst] = useState('The Dostination Team');
  const [signerTitle, setSignerTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState(null);
  const [resendReport, setResendReport] = useState(null);

  const fetchRows = async (opts = {}) => {
    setLoading(true);
    try {
      const params = {};
      if (opts.unmask) params.unmask = 1;
      if (onlySubmitted) params.only_submitted = 1;
      const { data } = await api.get(
        '/admin/incident/pre-bundle89-recipients', { params },
      );
      setRows(data.recipients || []);
      setSummary(data.summary || null);
      if (opts.unmask) setUnmasked(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load recipients.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRows(); }, [onlySubmitted]);

  const selectedIds = useMemo(
    () => Object.entries(selected).filter(([, v]) => v).map(([k]) => k),
    [selected],
  );

  const toggleAll = () => {
    if (selectedIds.length === rows.length) setSelected({});
    else {
      const next = {};
      rows.forEach((r) => { next[r.invitation_id] = true; });
      setSelected(next);
    }
  };

  const doResend = async (dry_run = false) => {
    if (!selectedIds.length) {
      toast.error('Select at least one row.');
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(
        '/admin/incident/pre-bundle89-recipients/resend',
        {
          invitation_ids: selectedIds,
          signer_first_name: signerFirst,
          signer_title: signerTitle,
          dry_run,
        },
      );
      setResendReport(data);
      toast.success(
        (dry_run ? 'Dry-run complete. ' : 'Reissue complete. ')
        + `${data.sent}/${data.attempted} processed.`,
      );
      // Bundle 106 - dry-run must PRESERVE the selection so ops can
      // review the report and hit Reissue with the same rows. Only
      // an actual reissue clears the selection.
      if (!dry_run) {
        setSelected({});
        fetchRows();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Resend failed.');
    } finally {
      setBusy(false);
    }
  };

  const exportCsv = async () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/incident/pre-bundle89-recipients.csv`;
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.click();
  };

  return (
    <div className="p-8 space-y-6 max-w-7xl"
          data-testid="admin-incident-pre-bundle89-page">
      <div className="text-xs text-neutral-500">
        <Link to="/admin/surveys" className="hover:text-primary-900">
          <ChevronLeft className="h-3 w-3 inline" /> Survey Ops
        </Link>
      </div>
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-red-700 border border-red-200 bg-red-50 rounded-full px-2 py-0.5"
                data-testid="pre89-hazard-strip">
            <AlertOctagon className="h-3 w-3" /> Survey data hygiene
          </div>
          <h1 className="font-display text-3xl font-bold text-primary-900 mt-2">
            Pre-Bundle-89 recipients
          </h1>
          <p className="text-neutral-600 text-sm max-w-2xl">
            Invitations minted before the current canonical question set was
            pinned. These attendees answered (or could answer) an older
            version of the survey. Select rows and Reissue to send them a
            fresh Bundle 89+ link. Old responses are preserved for audit
            and excluded from public analytics.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => (unmasked ? setUnmasked(false)
                                 : fetchRows({ unmask: true }))}
            data-testid="pre89-unmask-btn"
          >
            {unmasked ? <EyeOff className="h-4 w-4 mr-2" />
                       : <Eye className="h-4 w-4 mr-2" />}
            {unmasked ? 'Mask emails' : 'Unmask emails'}
          </Button>
          <Button variant="outline" onClick={exportCsv}
                    data-testid="pre89-csv-btn">
            <Download className="h-4 w-4 mr-2" /> CSV
          </Button>
        </div>
      </div>

      {summary ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3"
              data-testid="pre89-summary">
          <Card><CardContent className="p-4">
            <div className="text-xs uppercase tracking-widest text-neutral-500">Total mismatched</div>
            <div className="text-2xl font-display font-bold text-primary-900 mt-1"
                  data-testid="summary-total">{summary.total_mismatched}</div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="text-xs uppercase tracking-widest text-red-700">Submitted on old schema</div>
            <div className="text-2xl font-display font-bold text-red-800 mt-1"
                  data-testid="summary-submitted-old">{summary.submitted_on_old_schema}</div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="text-xs uppercase tracking-widest text-neutral-500">Distinct events</div>
            <div className="text-2xl font-display font-bold text-primary-900 mt-1">
              {summary.distinct_events}
            </div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="text-xs uppercase tracking-widest text-neutral-500">Last send</div>
            <div className="text-sm font-mono text-primary-900 mt-1">
              {summary.last_send ? new Date(summary.last_send).toLocaleString() : '-'}
            </div>
          </CardContent></Card>
        </div>
      ) : null}

      <Card><CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-3 flex-wrap">
          <label className="flex items-center gap-2 text-sm text-primary-800 cursor-pointer">
            <Checkbox
              checked={onlySubmitted}
              onCheckedChange={(v) => setOnlySubmitted(!!v)}
              data-testid="pre89-only-submitted-toggle"
            />
            Only show submitted-on-old-schema rows (highest priority)
          </label>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label htmlFor="signer-first">Signer first name</Label>
            <Input id="signer-first" value={signerFirst}
                    onChange={(e) => setSignerFirst(e.target.value)}
                    data-testid="pre89-signer-first-input" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="signer-title">Signer title (optional)</Label>
            <Input id="signer-title" value={signerTitle}
                    onChange={(e) => setSignerTitle(e.target.value)}
                    data-testid="pre89-signer-title-input" />
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline" disabled={busy || !selectedIds.length}
            onClick={() => doResend(true)}
            data-testid="pre89-dryrun-btn"
          >Dry run ({selectedIds.length})</Button>
          <Button
            className="bg-primary-900 text-white hover:bg-primary-800"
            disabled={busy || !selectedIds.length}
            onClick={() => doResend(false)}
            data-testid="pre89-resend-btn"
          >
            <Send className="h-4 w-4 mr-2" />
            Reissue on new schema ({selectedIds.length})
          </Button>
        </div>
        {resendReport ? (() => {
          const attempted = resendReport.attempted || 0;
          const processed = resendReport.sent || 0;
          const rejected = attempted - processed;
          const reasons = resendReport.rejected_by_reason || {};
          const failed = rejected > 0;
          const reasonKeys = Object.keys(reasons);
          const known = {
            rate_limited: 'Recipient hit the 5-per-hour email rate limit. Wait an hour and retry, or ask the platform admin to bump the limit.',
            stubbed_no_transport: 'Resend transport is not configured on the running pod (missing RESEND_API_KEY env var). Contact platform admin.',
            suppressed_synthetic_recipient: 'Recipient is on a synthetic test domain and cannot receive real email.',
            no_email_on_registration: 'The registration row has no email field. Fix the registration and retry.',
            already_replaced: 'A previous reissue already replaced this invitation. Refresh the list to remove it.',
            invitation_failed: 'The database upsert for the new invitation failed. Check backend logs.',
            not_found: 'The selected invitation_id no longer exists.',
            missing_to: 'Send transport received an empty recipient.',
          };
          return (
            <div className={`text-xs rounded-md p-3 border ${failed ? 'text-red-800 bg-red-50 border-red-200' : 'text-primary-800 bg-neutral-50 border-neutral-200'}`}
                  data-testid="pre89-resend-report">
              <div className="font-semibold">
                {resendReport.dry_run ? 'Dry-run: ' : ''}
                Attempted {attempted}, processed {processed}.
              </div>
              {failed ? (
                <div className="mt-2 space-y-1" data-testid="pre89-resend-rejection-breakdown">
                  <div className="uppercase tracking-widest text-[10px] opacity-70">
                    Rejection breakdown ({rejected} blocked)
                  </div>
                  <ul className="ml-4 list-disc">
                    {reasonKeys.map((k) => (
                      <li key={k} data-testid={`pre89-reason-${k}`}>
                        <span className="font-mono">{k}</span>: {reasons[k]}
                        {known[k] ? <div className="text-[11px] opacity-90 italic">{known[k]}</div> : null}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          );
        })() : null}
      </CardContent></Card>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm" data-testid="pre89-table">
            <thead className="bg-neutral-50 text-left text-xs uppercase tracking-widest text-neutral-500">
              <tr>
                <th className="p-3 w-8">
                  <Checkbox
                    checked={selectedIds.length > 0 && selectedIds.length === rows.length}
                    onCheckedChange={toggleAll}
                    aria-label="Select all rows"
                    data-testid="pre89-select-all"
                  />
                </th>
                <th className="p-3">Recipient</th>
                <th className="p-3">Email</th>
                <th className="p-3">Event</th>
                <th className="p-3">Sent</th>
                <th className="p-3">Submitted</th>
                <th className="p-3">Response</th>
                <th className="p-3">Drift</th>
                <th className="p-3">Reissue</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={10} className="p-6 text-center text-neutral-500">Loading...</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={10} className="p-6 text-center text-neutral-500"
                              data-testid="pre89-empty">
                  No pre-Bundle-89 recipients. Every invitation is on the current schema.
                </td></tr>
              ) : rows.map((r) => (
                <tr key={r.invitation_id}
                      className="border-t border-neutral-100 hover:bg-neutral-50"
                      data-testid={`pre89-row-${r.invitation_id}`}>
                  <td className="p-3">
                    <Checkbox
                      checked={!!selected[r.invitation_id]}
                      onCheckedChange={(v) => setSelected((s) => ({
                        ...s, [r.invitation_id]: !!v,
                      }))}
                      aria-label={`Select ${r.recipient_name}`}
                      data-testid={`pre89-select-${r.invitation_id}`}
                    />
                  </td>
                  <td className="p-3 text-primary-900 font-medium">{r.recipient_name}</td>
                  <td className="p-3 font-mono text-xs text-neutral-700">{r.email}</td>
                  <td className="p-3 text-neutral-700">{r.event_name}</td>
                  <td className="p-3 text-xs text-neutral-600">
                    {r.sent_at ? new Date(r.sent_at).toLocaleDateString() : '-'}
                  </td>
                  <td className="p-3 text-xs text-neutral-600">
                    {r.submitted_at ? new Date(r.submitted_at).toLocaleDateString() : '-'}
                  </td>
                  <td className="p-3">
                    <Badge className={`${RESP_BADGE[r.response_status] || RESP_BADGE.no_response} border`}
                            data-testid={`pre89-response-badge-${r.invitation_id}`}>
                      {r.response_status.replace(/_/g, ' ')}
                    </Badge>
                  </td>
                  <td className="p-3">
                    <div className="text-[10px] text-red-700 space-y-0.5"
                          data-testid={`pre89-drift-${r.invitation_id}`}>
                      {(r.schema_drift_reasons || []).slice(0, 3).map((x, i) => (
                        <div key={i}>{x}</div>
                      ))}
                      {r.schema_drift_reasons?.length > 3 ? (
                        <div className="text-neutral-500">+{r.schema_drift_reasons.length - 3} more</div>
                      ) : null}
                    </div>
                  </td>
                  <td className="p-3">
                    <Badge className="bg-neutral-100 text-neutral-700 border border-neutral-200">
                      {r.apology_resend_status}
                    </Badge>
                  </td>
                  <td className="p-3">
                    {r.response_id ? (
                      <Button size="sm" variant="ghost"
                              onClick={() => setDetail(r)}
                              data-testid={`pre89-view-${r.invitation_id}`}
                      >View old response</Button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Dialog open={!!detail} onOpenChange={(v) => { if (!v) setDetail(null); }}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto"
                        data-testid="pre89-detail-dialog">
          <DialogHeader>
            <DialogTitle>Old-schema response - {detail?.recipient_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 text-sm">
            <div className="text-xs text-neutral-500">
              Response id: <span className="font-mono">{detail?.response_id}</span>
            </div>
            <div className="text-xs text-neutral-500">
              Drift reasons: {(detail?.schema_drift_reasons || []).join(', ')}
            </div>
            <div className="text-xs text-neutral-500">
              This response has been set aside for internal audit and is
              excluded from public analytics. A fresh Bundle 89+ invitation
              will be sent when you Reissue.
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminIncidentPreBundle89Page;
